import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { quoteLinesApi } from '../api';
import { centsToDollarInput, dollarsToCents } from '../constants';
import { MoneyInput } from './MoneyInput';
import { UNIT_TYPE_OPTIONS, COST_CLASS_OPTIONS } from '../priceBookConstants';
import { PRICE_SOURCE_LABELS } from '../quoteConstants';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const labelCls = 'text-xs text-gray-400 mb-1 block';

export const QuoteLineEditDialog = ({ open, onOpenChange, quote, line, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && line) {
      setForm({
        name: line.name || '', description: line.description || '', section_name: line.section_name || '',
        category: line.category || '', cost_classification: line.cost_classification || '',
        unit_type: line.unit_type, custom_unit_label: line.custom_unit_label || '',
        quantity_decimal: line.quantity_decimal || '1',
        customer_price: centsToDollarInput(line.customer_unit_price_cents),
        internal_notes: line.internal_notes || '',
      });
    }
  }, [open, line]);

  if (!form || !line) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const priceChanged = dollarsToCents(form.customer_price) !== (line.customer_unit_price_cents ?? null);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Line name is required');
    setSaving(true);
    try {
      await quoteLinesApi.update(quote.id, line.id, {
        name: form.name, description: form.description || null, section_name: form.section_name || null,
        category: form.category || null, cost_classification: form.cost_classification || null,
        unit_type: form.unit_type, custom_unit_label: form.custom_unit_label || null,
        quantity_decimal: form.quantity_decimal,
        customer_unit_price_cents: dollarsToCents(form.customer_price),
        internal_notes: form.internal_notes || null,
      });
      toast.success('Line updated');
      onOpenChange(false);
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not update line');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="line-edit-dialog">
        <DialogHeader><DialogTitle>Edit Quote Line</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {line.source_assembly_name_snapshot && (
            <p className="text-xs text-gray-500">From assembly: {line.source_assembly_name_snapshot} · Editing this line never changes the Price Book.</p>
          )}
          <div><label className={labelCls}>Name *</label><Input value={form.name} onChange={set('name')} className={inputCls} data-testid="line-edit-name" /></div>
          <div><label className={labelCls}>Description</label><Textarea value={form.description} onChange={set('description')} rows={2} className={inputCls} data-testid="line-edit-description" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={labelCls}>Quantity *</label><Input value={form.quantity_decimal} onChange={set('quantity_decimal')} className={inputCls} data-testid="line-edit-qty" /></div>
            <div>
              <label className={labelCls}>Unit</label>
              <select value={form.unit_type} onChange={set('unit_type')} className={selectCls} data-testid="line-edit-unit">
                {UNIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {form.unit_type === 'other' ? (
              <div><label className={labelCls}>Custom Unit</label><Input value={form.custom_unit_label} onChange={set('custom_unit_label')} className={inputCls} data-testid="line-edit-unit-label" /></div>
            ) : (
              <div><label className={labelCls}>Section</label><Input value={form.section_name} onChange={set('section_name')} className={inputCls} data-testid="line-edit-section" /></div>
            )}
          </div>
          <div><label className={labelCls}>Customer Unit Price ($)</label><MoneyInput value={form.customer_price} onChange={(v) => setForm((x) => ({ ...x, customer_price: v }))} placeholder="Blank = unknown" className={inputCls} data-testid="line-edit-customer-price" /></div>
          <p className="text-xs text-gray-500">
            Price source: <span className="text-gray-300">{PRICE_SOURCE_LABELS[line.price_source] || line.price_source}</span>
            {priceChanged && <span className="text-amber-400"> · Changing the customer price marks this line as Custom pricing. The Price Book is not affected.</span>}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Category</label><Input value={form.category} onChange={set('category')} className={inputCls} data-testid="line-edit-category" /></div>
            <div>
              <label className={labelCls}>Classification</label>
              <select value={form.cost_classification} onChange={set('cost_classification')} className={selectCls} data-testid="line-edit-class">
                <option value="">None</option>
                {COST_CLASS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div><label className={labelCls}>Internal Notes (never shown to customer)</label><Textarea value={form.internal_notes} onChange={set('internal_notes')} rows={2} className={inputCls} data-testid="line-edit-notes" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300" data-testid="line-edit-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="line-edit-save-btn">
              {saving && <Loader2 size={14} className="animate-spin" />} Save Line
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
